import {
  StrictMode,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';
import App from '../renderer/App';
import type { JobAppLinkRoute } from '../shared/app-links';
import '../renderer/styles.css';
import { installTooltipPreference } from '../renderer/tooltip-preference';
import { blockHiddenHostEvents } from './hidden-host-events';
import { installDesktopAdapter } from './desktop-adapter';
import { followTheme } from './paige-theme';
import { trackProduct } from './product-tracker';
import { IntakeSheet } from './IntakeSheet';
import { JobLimitNotice } from './JobLimitNotice';
import { UploadProgress } from './UploadProgress';
import { EngineUpdatingNotice } from './EngineUpdatingNotice';
import { ArrivedOffer } from './ArrivedOffer';
import { WorkbenchSkeleton } from './WorkbenchSkeleton';
import { useBridge } from './workbench-bridge';
import { forgetEndedSetup } from './folder-analysis';
import {
  pinSetupRequest,
  useSetupOpening,
  useSetupShown,
  type SetupRequest,
} from './setup-request';
import type {
  PaigeIntakeKind,
  PaigeUploadProgress,
  PaigeWorkbenchBridge,
  PaigeWorkbenchPerson,
} from './paige.global';
import './paige.global';
import './workbench.css';

export interface CapturePointWorkbenchProps {
  /** The CapturePoint job (workspace job id) to open in the review workbench; none when creating one. */
  jobId?: string;
  /** Where `/engine` and `/pages` answer; the page's own origin by default. */
  engineOrigin?: string;
  /** The tenant to act in when the channel is the API's. */
  tenant?: string;
  /** Which of the job's screens opens: documents (capture) or CapturePoint's job setup. */
  mode?: 'capture' | 'setup';
  /**
   * Open the job on its setup, on Overview (`true`, the jobs home card menu's Job setup) or on Export
   * with `openDestination`'s sheet open (`'export'`, back from a storage sign-in; `setup-request.ts`).
   * `onSetupShown` answers once the App has opened, for the host to drop its request (the review
   * route's `?setup=`) so a reload opens the documents.
   */
  openSetup?: SetupRequest;
  openDestination?: string | undefined;
  onSetupShown?: () => void;
  /** Where the Paige logo in the workbench leads: back up to Paige's job dashboard. */
  onLeave?: () => void;
  /** The API root for jobs the workbench creates; with `onCreated`, mounts on the new-job dialog. */
  apiOrigin?: string;
  onCreated?: (paigeJobId: string) => void;
  /** Where New job leads from a job's own route: Paige's new-job route (the job is the page's own). */
  onNewJob?: (mode?: string) => void;
  /** Where another job (by its workspace job id) opens from a job's own route: that job's route. */
  onOpenJob?: (workspaceJobId: string) => void;
  /** The signed-in person, named on the leases they hold (ADR-0009). */
  user?: { id: string; name: string };
  /** The same person for the workspace menu: name, company, Sign out (IMPLEMENTATION.md sprint 2, row 25). */
  person?: PaigeWorkbenchPerson;
  /** Whether the person may change the job's setup (admin, manager, staff); unknown leaves it to the API. */
  runsJobs?: boolean;
  /** Whether the person runs the company (admin, staff), who gets Paige Scan onto a computer; unknown is yes. */
  runsCompany?: boolean;
  /** Paige staff under a read-only support grant: reads everything, takes no lease, writes nothing. */
  readOnly?: boolean;
  /**
   * A link the page was opened with (app-links.ts): `tour` is Take a 2-minute tour; a job place opens
   * that place of the routed job (Learning › Results, Documents › Teach, Lookup lists, Review thresholds).
   */
  appLink?: 'tour' | JobAppLinkRoute;
  /** The new-job dialog opens on its name step (My own documents), not on how to start. */
  createStart?: 'name';
}

/**
 * CapturePoint's renderer, mounted inside Paige: the desktop bridge is replaced by an adapter that
 * speaks to the engine host over HTTP, the workspace session is pinned to the job the route names,
 * and the App renders exactly as the desktop does (docs/engineering/workbench-port.md).
 */
export function CapturePointWorkbench(props: CapturePointWorkbenchProps): ReactNode {
  const { jobId } = props;
  const onLeave = useLeave(jobId, props.onLeave);
  const [intake, setIntake] = useState<PaigeIntakeKind | null>(null);
  const [upload, setUpload] = useState<PaigeUploadProgress | null>(null);
  const [jobLimit, setJobLimit] = useState<number | null>(null);
  const opened = useHostedApp({ ...props, onLeave }, setIntake, setUpload, setJobLimit);
  useSetupShown(opened, props.openSetup, props.onSetupShown);
  return (
    // Keyed by the job: the App reads its session once, on mount. Not by the mode: the route's mode
    // follows the job's state, and a setup's own Save moves the job out of Setup, so a key with the
    // mode in it remounted the App in the middle of the setup's handoff to Documents, and the new
    // App wrote the job back as provisional (2026-10-01, workbench-port.md). CapturePoint's App
    // changes its own mode; it is never replaced under the person.
    <div className="paige-capturepoint" key={jobId ?? 'new'} onClickCapture={leaveOnBrand(onLeave)}>
      <StrictMode>
        <App />
      </StrictMode>
      {!opened && <WorkbenchSkeleton />}
      {upload ? <UploadProgress progress={upload} /> : <EngineUpdatingNotice />}
      {jobId && !upload && <SetupLines {...props} />}
      {intake && <IntakeSheet kind={intake} onClose={() => setIntake(null)} />}
      {jobLimit !== null && <JobLimitNotice limit={jobLimit} onClose={() => setJobLimit(null)} />}
    </div>
  );
}

/** What a job on its setup is told in one line: what arrived for it. */
function SetupLines(props: CapturePointWorkbenchProps): ReactNode {
  const engineOrigin = props.engineOrigin ?? '';
  const inSetup = props.mode === 'setup';
  return <ArrivedOffer channel={{ engineOrigin, tenant: props.tenant }} inSetup={inSetup} />;
}

/**
 * The adapter, the pinned session and the host installed before the App renders; whether the App
 * has its first real screen yet. Until it has, the mount shows the workbench's shape, never the
 * App's own steps (the job library before the jobs list, "Opening..." before the documents).
 */
function useHostedApp(
  props: CapturePointWorkbenchProps,
  showIntake: (kind: PaigeIntakeKind) => void,
  showUpload: (progress: PaigeUploadProgress | null) => void,
  jobLimit: (limit: number) => void,
): boolean {
  const {
    jobId,
    engineOrigin = '',
    tenant,
    mode = 'capture',
    openSetup = false,
    apiOrigin,
  } = props;
  const [opened, markOpened] = useOpened(jobId);
  const openedOnSetup = useSetupOpening(openSetup, mode, markOpened);
  const bridge = useBridge(props, { showIntake, showUpload, opened: openedOnSetup, jobLimit });
  useMemo(() => {
    installDesktopAdapter({ engineOrigin, tenant, apiOrigin, bridge });
  }, [jobId, engineOrigin, tenant, apiOrigin, bridge]);
  usePinnedSession(props);
  useEffect(() => installHost(bridge), [bridge]);
  return opened;
}

/** The session the App reads when it mounts; a later mode is the App's own to change. */
function usePinnedSession(props: CapturePointWorkbenchProps): void {
  const { jobId, mode = 'capture', openSetup = false, openDestination } = props;
  useMemo(
    () => pinWorkspaceSession({ jobId, mode, openSetup, openDestination }),
    [jobId, mode, openSetup, openDestination],
  );
}

/** Whether the App has opened, and the bridge's way of saying so; an opened job is a review.opened event. */
function useOpened(jobId: string | undefined): [boolean, () => void] {
  const [opened, setOpened] = useState(false);
  const markOpened = useCallback(() => {
    // A job opened for review; a job being created (no id yet) is not one.
    if (jobId) trackProduct('review.opened');
    setOpened(true);
  }, [jobId]);
  return [opened, markOpened];
}

/**
 * Leaving the job's route: a setup from samples that ended there without a proposal is forgotten,
 * so the job opens on its start screen next time rather than on the failure (unfinished-setup.ts).
 */
function useLeave(jobId: string | undefined, onLeave: (() => void) | undefined) {
  return useMemo(
    () =>
      onLeave &&
      (() => {
        if (jobId) forgetEndedSetup(jobId);
        onLeave();
      }),
    [jobId, onLeave],
  );
}

/** The desktop's brand button opens its own jobs home; in Paige that level is the job dashboard. */
function leaveOnBrand(onLeave: (() => void) | undefined): (event: MouseEvent) => void {
  return (event) => {
    if (onLeave && (event.target as HTMLElement).closest('.brand-button')) {
      event.preventDefault();
      event.stopPropagation();
      onLeave();
    }
  };
}

/** What the desktop's main.tsx installed around <App/>: the host class, tooltips, colour scheme, theme, the bridge; returns the teardown. */
function installHost(bridge: PaigeWorkbenchBridge): () => void {
  const root = document.documentElement;
  window.paigeWorkbench = bridge;
  root.classList.add('capturepoint-host');
  root.style.colorScheme = root.dataset['theme'] ?? '';
  const stopTooltipPreference = installTooltipPreference();
  const stopTheme = followTheme();
  // Settings screens Paige hides (License's "Cloud credits", Performance) are never opened by the desktop's events.
  const stopHiddenEvents = blockHiddenHostEvents();
  return () => {
    stopHiddenEvents();
    delete window.paigeWorkbench;
    stopTheme();
    stopTooltipPreference();
    root.classList.remove('capturepoint-host');
    root.style.colorScheme = '';
  };
}

/**
 * The renderer restores its screen and job from this session key (App.tsx
 * `rendererWorkspaceSessionKey`); a requested setup is a one-shot key the capture screen reads when
 * it mounts (`setupRequestKey`), withdrawn when the host drops its request so a later mount opens the documents.
 */
function pinWorkspaceSession({
  jobId,
  mode,
  openSetup,
  openDestination,
}: {
  jobId: string | undefined;
  mode: 'capture' | 'setup';
  openSetup: SetupRequest;
  openDestination: string | undefined;
}): void {
  try {
    window.sessionStorage.setItem(
      'capturepoint:renderer-workspace:v1',
      JSON.stringify(
        jobId ? { screen: 'job', jobMode: mode, selectedId: jobId } : { screen: 'jobs' },
      ),
    );
    pinSetupRequest(openSetup, openDestination);
    // The desktop's first-run tour explains scanning and folders; Paige's intake is its own screen.
    window.localStorage.setItem('cpv6-product-learning-introduction-v1', 'dismissed');
  } catch {
    // Without session storage the App falls back to its first job; the route still names ours.
  }
}
